import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { ReactNode, useState } from "react";
import {
  ArtifactType,
  GetProfileArtifactsQueryQuery,
  GetProfileArtifactsQueryQueryVariables,
} from "types/graphql-schema";
import { GetProfileQueryQuery, GoalState } from "types/graphql-schema";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import Artifact from "@apps/artifact/artifact";
import { getExplorerFiltersUrl } from "@apps/explorer/helpers";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import ProfileTabEmptyState from "../../components/profile-tab-empty-state";
import ProfileContainer from "../../profile-container";
import ProfileEmptyState from "../../profile-empty-state";
import getProfileArtifactsQuery from "../graphql/get-profile-artifacts-query";
import ProfileReviews from "./profile-reviews";

type ProfileForArtifacts = NonNullable<GetProfileQueryQuery["user"]>;

const ViewAllButton = ({
  to,
  artifactType,
}: {
  to: string;
  artifactType: ArtifactType;
}) => {
  const label = useLabel();
  return (
    <Button theme={buttonTheme.text} size={ButtonSize.medium} to={to}>
      View All {label(artifactType, { pluralize: true, titleCase: true })}
    </Button>
  );
};

const CreateButton = ({
  text,
  onClick,
}: {
  text: string;
  onClick: () => void;
}) => (
  <Button
    theme={buttonTheme.text}
    size={ButtonSize.medium}
    onClick={onClick}
    text={text}
  />
);

export const ProfileActionItems = ({
  profile,
  bare = false,
}: {
  profile: ProfileForArtifacts;
  bare?: boolean;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  if (!organization.featureFlags.actionItems) return null;

  const incompleteStates = compact(organization.actionItemStates)
    .filter((state) => !state.isComplete)
    .map((state) => state.value);

  return (
    <>
      <ProfileArtifactsContent
        artifactType={ArtifactType.ActionItem}
        topRightContent={
          bare ? (
            <CreateButton
              text={`Create ${label("action item", { titleCase: true })}`}
              onClick={() => setShowCreateDialog(true)}
            />
          ) : (
            <ViewAllButton
              to={getExplorerFiltersUrl({
                actionItemAssignee: profile.id,
                orderBy: "due_date",
                actionItemState: incompleteStates,
                type: ArtifactType.ActionItem,
              })}
              artifactType={ArtifactType.ActionItem}
            />
          )
        }
        title={`${label("action item", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          artifactType: ArtifactType.ActionItem,
          first: 25,
          actionItemIsComplete: false,
          actionItemAssignee: profile.id,
          orderBy: "due_date",
        }}
        bare={bare}
        emptyState={
          bare ? (
            <ProfileTabEmptyState
              title={`No ${label("action item", { titleCase: true, pluralize: true })} Created`}
              body={`${label("action item", { titleCase: true, pluralize: true })} will show up here as they get added.`}
              buttonText={`Create ${label("action item", { titleCase: true })}`}
              onButtonClick={() => setShowCreateDialog(true)}
            />
          ) : undefined
        }
      />
      {showCreateDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: ArtifactType.ActionItem,
            assignee: profile,
          }}
          onClose={() => setShowCreateDialog(false)}
          refetchQueries={[getProfileArtifactsQuery]}
        />
      )}
    </>
  );
};

export const ProfileGoals = ({
  profile,
  bare = false,
}: {
  profile: ProfileForArtifacts;
  bare?: boolean;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  if (!organization.featureFlags.goals) return null;

  return (
    <>
      <ProfileArtifactsContent
        artifactType={ArtifactType.Goal}
        topRightContent={
          bare ? (
            <CreateButton
              text={`Create ${label("goal", { titleCase: true })}`}
              onClick={() => setShowCreateDialog(true)}
            />
          ) : (
            <ViewAllButton
              to={`/goals/user/${profile.id}`}
              artifactType={ArtifactType.Goal}
            />
          )
        }
        title={`Open ${label("goal", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          goalOwners: [profile.id],
          goalStates: [GoalState.Open, GoalState.Draft],
          artifactType: ArtifactType.Goal,
          first: 25,
        }}
        bare={bare}
        emptyState={
          bare ? (
            <ProfileTabEmptyState
              title={`No ${label("goal", { titleCase: true, pluralize: true })} Created`}
              body={`${label("goal", { titleCase: true, pluralize: true })} will show up here as they get added.`}
              buttonText={`Create ${label("goal", { titleCase: true })}`}
              onButtonClick={() => setShowCreateDialog(true)}
            />
          ) : undefined
        }
      />
      {showCreateDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: ArtifactType.Goal,
            owners: [profile],
          }}
          onClose={() => setShowCreateDialog(false)}
          refetchQueries={[getProfileArtifactsQuery]}
        />
      )}
    </>
  );
};

export const ProfileFeedback = ({
  profile,
  bare = false,
}: {
  profile: ProfileForArtifacts;
  bare?: boolean;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  if (!organization.featureFlags.feedbacks) return null;

  return (
    <>
      <ProfileArtifactsContent
        artifactType={ArtifactType.Feedback}
        topRightContent={
          bare ? (
            <CreateButton
              text={`Give ${label("feedback", { titleCase: true })}`}
              onClick={() => setShowCreateDialog(true)}
            />
          ) : (
            <ViewAllButton
              to={`/feedbacks/received?userId=${profile.id}&dateRange=all`}
              artifactType={ArtifactType.Feedback}
            />
          )
        }
        title={`${label("feedback", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          artifactType: ArtifactType.Feedback,
          first: 25,
          feedbackRecipient: profile.id,
          orderBy: "-created",
        }}
        bare={bare}
        emptyState={
          bare ? (
            <ProfileTabEmptyState
              title={`No ${label("feedback", { titleCase: true })} Given`}
              body={`${label("feedback", { titleCase: true })} will show up here as it gets shared.`}
              buttonText={`Give ${label("feedback", { titleCase: true })}`}
              onButtonClick={() => setShowCreateDialog(true)}
            />
          ) : undefined
        }
      />
      {showCreateDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: ArtifactType.Feedback,
            feedbackRecipients: [profile],
          }}
          onClose={() => setShowCreateDialog(false)}
          refetchQueries={[getProfileArtifactsQuery]}
        />
      )}
    </>
  );
};

export const ProfileRecognitions = ({
  profile,
  bare = false,
}: {
  profile: ProfileForArtifacts;
  bare?: boolean;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  if (!organization.featureFlags.recognitions) return null;

  return (
    <>
      <ProfileArtifactsContent
        artifactType={ArtifactType.Recognition}
        topRightContent={
          bare ? (
            <CreateButton
              text={`Give ${label("recognition", { titleCase: true })}`}
              onClick={() => setShowCreateDialog(true)}
            />
          ) : (
            <ViewAllButton
              to={`/recognition-board?recipientId=${profile.id}`}
              artifactType={ArtifactType.Recognition}
            />
          )
        }
        title={`${label("recognition", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          recognitionRecipient: profile.id,
          artifactType: ArtifactType.Recognition,
          first: 25,
          orderBy: "-created",
        }}
        bare={bare}
        emptyState={
          bare ? (
            <ProfileTabEmptyState
              title={`No ${label("recognition", { titleCase: true })} Given`}
              body={`${label("recognition", { titleCase: true })} will show up here as it gets shared.`}
              buttonText={`Give ${label("recognition", { titleCase: true })}`}
              onButtonClick={() => setShowCreateDialog(true)}
            />
          ) : undefined
        }
      />
      {showCreateDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: ArtifactType.Recognition,
            recognitionRecipients: [profile],
          }}
          onClose={() => setShowCreateDialog(false)}
          refetchQueries={[getProfileArtifactsQuery]}
        />
      )}
    </>
  );
};

const ProfileArtifacts = ({ profile }: { profile: ProfileForArtifacts }) => {
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );
  const canViewReviews = isAdmin || isManager || profile.id === currentUser.id;

  return compact([
    <ProfileGoals key="goals" profile={profile} />,
    <ProfileActionItems key="action-items" profile={profile} />,
    canViewReviews && <ProfileReviews profileId={profile.id} key="reviews" />,
    <ProfileRecognitions key="recognitions" profile={profile} />,
    <ProfileFeedback key="feedbacks" profile={profile} />,
  ]);
};

export const ProfileArtifactsContent = ({
  artifactType,
  topRightContent,
  title,
  variables,
  bare = false,
  emptyState,
}: {
  artifactType: ArtifactType;
  title: string;
  topRightContent?: ReactNode;
  variables: GetProfileArtifactsQueryQueryVariables;
  bare?: boolean;
  emptyState?: ReactNode;
}) => {
  const label = useLabel();
  const { data, loading, fetchMore } = useQuery<
    GetProfileArtifactsQueryQuery,
    GetProfileArtifactsQueryQueryVariables
  >(getProfileArtifactsQuery, {
    variables: variables,
  });

  const artifacts = data?.artifacts ? assertEdgesNonNull(data.artifacts) : [];

  const handleClickLoadMore = () => {
    if (data?.artifacts?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.artifacts?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  return (
    <>
      <ProfileContainer
        title={title}
        bare={bare}
        topRightContent={topRightContent}
      >
        {!data && loading ? (
          <Loading>Loading...</Loading>
        ) : artifacts.length > 0 ? (
          <div className="flex flex-col gap-4 w-full">
            {artifacts.map((artifact) => (
              <Artifact
                key={artifact.id}
                artifact={artifact}
                hideActionDropdown
              />
            ))}
            {data?.artifacts?.pageInfo?.hasNextPage && (
              <div className="flex justify-center gap-2">
                <Button
                  size={ButtonSize.medium}
                  text={loading ? "loading..." : "load more"}
                  onClick={handleClickLoadMore}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        ) : emptyState ? (
          emptyState
        ) : (
          <ProfileEmptyState>
            No {label(artifactType, { pluralize: true })}.
          </ProfileEmptyState>
        )}
      </ProfileContainer>
    </>
  );
};

export default ProfileArtifacts;
